import type * as React from 'react';
import { cn } from 'cn';

interface ProgressProps extends Omit<React.ComponentProps<'div'>, 'children'> {
  /** 0–100. Values outside the range are clamped. */
  value: number;
  /** Before there is anything to measure: a pulsing third-width bar instead of a fill. */
  indeterminate?: boolean;
  /** Extra classes for the fill, e.g. a different colour once complete. */
  indicatorClassName?: string;
}

/** Thin horizontal progress bar: grey track, violet fill, teal at 100%. */
function Progress({ value, indeterminate = false, className, indicatorClassName, ...props }: ProgressProps) {
  const clamped = Math.min(100, Math.max(0, value));
  return (
    <div
      data-slot="progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : clamped}
      className={cn('h-1 w-full overflow-hidden rounded-full bg-gray-200', className)}
      {...props}
    >
      {indeterminate ? (
        <div className={cn('h-full w-1/3 animate-pulse rounded-full bg-violet-400', indicatorClassName)} />
      ) : (
        <div
          data-slot="progress-indicator"
          className={cn(
            'h-full rounded-full transition-all',
            clamped === 100 ? 'bg-teal-500' : 'bg-violet-600',
            indicatorClassName,
          )}
          style={{ width: `${clamped}%` }}
        />
      )}
    </div>
  );
}

export { Progress };
